/**
 * Home: más vistos, catálogo y pendientes de aprobación.
 */
import React, { useEffect, useMemo, useState } from 'react';
import ChartLineIcon from '../assets/icons/chart-line.svg';
import { useAuth } from '../AuthContext';
import { useData } from '../DataContext';
import { useNavigation } from '../NavigationContext';
import { isManualApprovalLock } from '../approvalLockUtils';
import { ink, studio, SearchField } from './admin/studioUi';
import { warmEngine } from './admin/widget/engineConfig';
import { useIsNarrow } from '../useMediaQuery';

const toDate = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp.toDate) return timestamp.toDate();
    if (timestamp._seconds) return new Date(timestamp._seconds * 1000);
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
};

const formatDeadline = (timestamp) => {
    const date = toDate(timestamp);
    if (!date) return 'Sin fecha';
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
};

const deadlineTone = (timestamp) => {
    const date = toDate(timestamp);
    if (!date) return { label: 'Sin deadline', tone: 'muted', overdue: false };
    const startToday = new Date();
    startToday.setHours(0, 0, 0, 0);
    const startTarget = new Date(date);
    startTarget.setHours(0, 0, 0, 0);
    const days = Math.round((startTarget.getTime() - startToday.getTime()) / 86400000);
    const pretty = formatDeadline(timestamp);
    if (days < 0) return { label: `Vencido · ${pretty}`, tone: 'danger', overdue: true };
    if (days === 0) return { label: `Hoy · ${pretty}`, tone: 'warn', overdue: false };
    if (days === 1) return { label: `Mañana · ${pretty}`, tone: 'warn', overdue: false };
    if (days <= 7) return { label: `En ${days} días · ${pretty}`, tone: 'warn', overdue: false };
    return { label: pretty, tone: 'ok', overdue: false };
};

const countsOf = (report) => report.adjustmentCounts || { pending: 0, completed: 0, validated: 0, total: 0 };

const DashboardGrid = () => {
    const { userData, userRole, userPermissions } = useAuth();
    const { dashboards, clients, projects, pendingApprovalReports } = useData();
    const { openReport, handleViewReportDetailsById } = useNavigation();
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [viewMode, setViewMode] = useState('list');
    const [hoveredRow, setHoveredRow] = useState(null);
    const [hoveredPendingRow, setHoveredPendingRow] = useState(null);
    const [hoveredAdjustmentRow, setHoveredAdjustmentRow] = useState(null);
    const [query, setQuery] = useState('');
    const narrow = useIsNarrow();
    /* Estilos de móvil encima de los de escritorio. */
    const m = (key) => (narrow && homeNarrow[key] ? { ...home[key], ...homeNarrow[key] } : home[key]);

    // Calienta el motor mientras el usuario elige: la primera apertura no paga arranque en frío.
    useEffect(() => { warmEngine(); }, []);

    const canManageAdjustments = userRole === 'admin' || userPermissions.reports_manage_adjustments === true;

    const firstName = (userData?.nombre || '').trim().split(' ')[0];
    const search = query.trim().toLowerCase();

    const publishedDashboards = useMemo(
        () => (dashboards || []).filter((item) => !item.isDraft),
        [dashboards]
    );

    const popularDashboards = useMemo(() => [...publishedDashboards]
        .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
        .slice(0, 4), [publishedDashboards]);

    const { accessibleClients, accessibleProjects } = useMemo(() => {
        if (!publishedDashboards.length) {
            return { accessibleClients: [], accessibleProjects: [] };
        }
        const clientIds = new Set(publishedDashboards.map((d) => d.clientId));
        const projectIds = new Set(publishedDashboards.map((d) => d.projectId));
        return {
            accessibleClients: (clients || []).filter((c) => clientIds.has(c.id)),
            accessibleProjects: (projects || []).filter((p) => projectIds.has(p.id)),
        };
    }, [publishedDashboards, clients, projects]);

    const projectCounts = useMemo(() => accessibleProjects.reduce((acc, project) => {
        if (project.clientId) acc[project.clientId] = (acc[project.clientId] || 0) + 1;
        return acc;
    }, {}), [accessibleProjects]);

    const reportCounts = useMemo(() => publishedDashboards.reduce((acc, report) => {
        if (report.projectId) acc[report.projectId] = (acc[report.projectId] || 0) + 1;
        return acc;
    }, {}), [publishedDashboards]);

    const reportsForSelectedProject = useMemo(() => {
        if (!selectedProject) return [];
        return publishedDashboards.filter((r) => r.projectId === selectedProject.id);
    }, [publishedDashboards, selectedProject]);

    const visibleClients = useMemo(() => {
        if (!search) return accessibleClients;
        return accessibleClients.filter((c) => (c.nombre || '').toLowerCase().includes(search));
    }, [accessibleClients, search]);

    const visibleProjects = useMemo(() => {
        if (!selectedClient) return [];
        const list = accessibleProjects.filter((p) => p.clientId === selectedClient.id);
        if (!search) return list;
        return list.filter((p) => (p.nombre || '').toLowerCase().includes(search));
    }, [accessibleProjects, selectedClient, search]);

    const visibleReports = useMemo(() => {
        if (!search) return reportsForSelectedProject;
        return reportsForSelectedProject.filter((r) => (r.reporteNombre || '').toLowerCase().includes(search));
    }, [reportsForSelectedProject, search]);

    const pendingCount = pendingApprovalReports?.length || 0;
    const hasCatalog = publishedDashboards.length > 0;

    const adjustmentRows = useMemo(() => {
        if (!canManageAdjustments) return [];
        return (publishedDashboards || [])
            .map((report) => ({ report, counts: countsOf(report) }))
            .filter(({ counts }) => (counts.pending || 0) > 0 || (counts.completed || 0) > 0)
            .sort((a, b) => (b.counts.pending - a.counts.pending) || (b.counts.completed - a.counts.completed));
    }, [canManageAdjustments, publishedDashboards]);

    const adjustmentOpenCount = useMemo(
        () => adjustmentRows.reduce((sum, row) => sum + (row.counts.pending || 0) + (row.counts.completed || 0), 0),
        [adjustmentRows]
    );

    const goClients = () => {
        setSelectedClient(null);
        setSelectedProject(null);
        setQuery('');
    };
    const goProjects = () => {
        setSelectedProject(null);
        setQuery('');
    };

    const catalogHint = selectedProject
        ? 'Abre un reporte de este proyecto'
        : selectedClient
            ? 'Elige un proyecto'
            : 'Elige un cliente para ver sus proyectos';

    const searchPlaceholder = selectedProject
        ? 'Buscar reporte…'
        : selectedClient
            ? 'Buscar proyecto…'
            : 'Buscar cliente…';

    return (
        <div style={m('page')}>
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Inicio</p>
                    <h2 style={studio.title}>{firstName ? `Hola, ${firstName}` : 'Tus reportes'}</h2>
                    <p style={studio.subtitle}>
                        Tus reportes, organizados por cliente y proyecto.
                    </p>
                </div>
                <div style={studio.stats}>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{publishedDashboards.length}</span>
                        <span style={studio.statLabel}>Reportes</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{accessibleClients.length}</span>
                        <span style={studio.statLabel}>Clientes</span>
                    </div>
                    {pendingCount > 0 && (
                        <button
                            type="button"
                            onClick={() => document.getElementById('home-pendientes')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                            style={{ ...studio.stat, borderLeftColor: ink.danger, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
                        >
                            <span style={studio.statValue}>{pendingCount}</span>
                            <span style={studio.statLabel}>Por aprobar</span>
                        </button>
                    )}
                    {adjustmentOpenCount > 0 && (
                        <button
                            type="button"
                            onClick={() => document.getElementById('home-ajustes')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                            style={{ ...studio.stat, borderLeftColor: '#e65100', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
                        >
                            <span style={studio.statValue}>{adjustmentOpenCount}</span>
                            <span style={studio.statLabel}>Ajustes abiertos</span>
                        </button>
                    )}
                </div>
            </div>

            {(pendingCount > 0 || adjustmentRows.length > 0) && (
                <div style={m('workRow')}>
                    {pendingCount > 0 && (
                        <div id="home-pendientes" style={m('pendingPanel')}>
                            <div style={home.pendingHead}>
                                <div>
                                    <p style={{ ...studio.kicker, color: ink.danger }}>Pendientes</p>
                                    <h3 style={home.pendingTitle}>Necesitan tu visto bueno</h3>
                                    <p style={home.pendingSub}>
                                        {pendingCount === 1
                                            ? '1 reporte espera tu aprobación.'
                                            : `${pendingCount} reportes esperan tu aprobación.`}
                                    </p>
                                </div>
                                <span style={home.pendingCount}>{pendingCount}</span>
                            </div>
                            <div style={home.pendingList}>
                                {pendingApprovalReports.map((report) => {
                                    const blocked = isManualApprovalLock(report);
                                    const due = deadlineTone(report.approvalDeadline);
                                    const hot = hoveredPendingRow === report.id;
                                    return (
                                        <article
                                            key={`pending-${report.id}`}
                                            style={{
                                                ...m('pendingCard'),
                                                ...(due.overdue ? home.pendingCardOverdue : {}),
                                                ...(hot ? home.pendingCardHot : {}),
                                            }}
                                            onMouseEnter={() => setHoveredPendingRow(report.id)}
                                            onMouseLeave={() => setHoveredPendingRow(null)}
                                        >
                                            <div style={home.pendingAccent} />
                                            <div style={home.pendingBody}>
                                                <div style={home.pendingName}>
                                                    {report.reporteNombre}
                                                    {blocked && <span style={home.blockedChip}>Bloqueado</span>}
                                                </div>
                                                <div style={home.pendingMeta}>
                                                    {report.cliente}
                                                    {report.proyecto ? ` · ${report.proyecto}` : ''}
                                                </div>
                                            </div>
                                            <div style={m('pendingAside')}>
                                                <span style={{ ...home.dueChip, ...home.dueChipTone[due.tone] }}>{due.label}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewReportDetailsById(report.id)}
                                                    style={blocked ? m('pendingGhost') : m('pendingCta')}
                                                >
                                                    {blocked ? 'Ver detalle' : 'Aprobar'}
                                                </button>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {adjustmentRows.length > 0 && (
                        <div id="home-ajustes" style={m('adjustPanel')}>
                            <div style={home.pendingHead}>
                                <div>
                                    <p style={{ ...studio.kicker, color: '#e65100' }}>Ajustes</p>
                                    <h3 style={home.pendingTitle}>Pendientes por reporte</h3>
                                    <p style={home.pendingSub}>
                                        {adjustmentRows.length === 1
                                            ? '1 reporte tiene ajustes abiertos.'
                                            : `${adjustmentRows.length} reportes tienen ajustes abiertos.`}
                                    </p>
                                </div>
                                <span style={home.adjustCount}>{adjustmentOpenCount}</span>
                            </div>
                            <div style={home.pendingList}>
                                {adjustmentRows.map(({ report, counts }) => {
                                    const hot = hoveredAdjustmentRow === report.id;
                                    return (
                                        <article
                                            key={`adjust-${report.id}`}
                                            style={{
                                                ...m('pendingCard'),
                                                ...(hot ? home.adjustCardHot : {}),
                                            }}
                                            onMouseEnter={() => setHoveredAdjustmentRow(report.id)}
                                            onMouseLeave={() => setHoveredAdjustmentRow(null)}
                                        >
                                            <div style={home.adjustAccent} />
                                            <div style={home.pendingBody}>
                                                <div style={home.pendingName}>{report.reporteNombre}</div>
                                                <div style={home.pendingMeta}>
                                                    {report.cliente}
                                                    {report.proyecto ? ` · ${report.proyecto}` : ''}
                                                </div>
                                            </div>
                                            <div style={m('pendingAside')}>
                                                {counts.pending > 0 && (
                                                    <span style={{ ...home.dueChip, ...home.dueChipTone.warn }}>
                                                        {counts.pending} pend.
                                                    </span>
                                                )}
                                                {counts.completed > 0 && (
                                                    <span style={{ ...home.dueChip, backgroundColor: ink.accentSoft, color: ink.accentText }}>
                                                        {counts.completed} por validar
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewReportDetailsById(report.id)}
                                                    style={m('adjustCta')}
                                                >
                                                    Revisar
                                                </button>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {hasCatalog && popularDashboards.length > 0 && (
                <div>
                    <p style={studio.kicker}>Atajos</p>
                    <h3 style={{ ...studio.formTitle, margin: '6px 0 14px' }}>Más vistos</h3>
                    <div style={m('popularGrid')}>
                        {popularDashboards.map((report, index) => (
                            <button
                                key={`popular-${report.id}`}
                                type="button"
                                onClick={() => openReport(report)}
                                style={m('popularCard')}
                            >
                                <div style={home.popularMeta}>
                                    <span style={home.rank}>{index + 1}</span>
                                    <span style={home.viewsBadge}>{report.viewCount || 0} vistas</span>
                                </div>
                                <div style={home.popularTitle}>{report.reporteNombre}</div>
                                <div style={home.popularSub}>{report.cliente} · {report.proyecto}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div style={studio.card}>
                <div style={{ ...studio.toolbar, marginBottom: 12, alignItems: 'flex-end' }}>
                    <div>
                        <p style={studio.kicker}>Catálogo</p>
                        <h3 style={{ ...studio.formTitle, margin: '6px 0 0' }}>
                            {selectedProject ? selectedProject.nombre : selectedClient ? selectedClient.nombre : 'Todos los reportes'}
                        </h3>
                        <div style={home.crumbs}>
                            <button type="button" onClick={goClients} style={home.crumbBtn}>Clientes</button>
                            {selectedClient && (
                                <>
                                    <span style={home.crumbSep}>/</span>
                                    <button type="button" onClick={goProjects} style={home.crumbBtn}>{selectedClient.nombre}</button>
                                </>
                            )}
                            {selectedProject && (
                                <>
                                    <span style={home.crumbSep}>/</span>
                                    <span style={home.crumbHere}>{selectedProject.nombre}</span>
                                </>
                            )}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        {selectedProject && (
                            <div style={studio.scopeBar}>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('list')}
                                    style={viewMode === 'list' ? home.scopeOn : home.scopeOff}
                                >
                                    Lista
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('grid')}
                                    style={viewMode === 'grid' ? home.scopeOn : home.scopeOff}
                                >
                                    Cuadrícula
                                </button>
                            </div>
                        )}
                        {(selectedClient || selectedProject) && (
                            <button type="button" onClick={selectedProject ? goProjects : goClients} style={studio.ghostBtn}>
                                Volver
                            </button>
                        )}
                    </div>
                </div>

                {hasCatalog && (
                    <SearchField
                        value={query}
                        onChange={setQuery}
                        placeholder={searchPlaceholder}
                        style={{ maxWidth: narrow ? '100%' : 420, marginBottom: 16 }}
                    />
                )}
                <p style={{ ...studio.hint, marginBottom: 16 }}>{hasCatalog ? catalogHint : 'Aún no tienes reportes asignados.'}</p>

                {!hasCatalog && (
                    <div style={studio.empty}>Cuando te asignen un reporte, aparecerá aquí.</div>
                )}

                {hasCatalog && selectedProject && viewMode === 'list' && (
                    visibleReports.length === 0 ? (
                        <div style={studio.empty}>Ningún reporte coincide.</div>
                    ) : (
                        <table style={studio.table}>
                            <thead>
                                <tr>
                                    <th style={studio.th}>Reporte</th>
                                    <th style={{ ...studio.th, width: '100px' }}>Vistas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleReports.map((report) => (
                                    <tr
                                        key={report.id}
                                        style={{
                                            ...(hoveredRow === report.id ? studio.trHover : {}),
                                            cursor: 'pointer',
                                        }}
                                        onMouseEnter={() => setHoveredRow(report.id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        onClick={() => openReport(report)}
                                    >
                                        <td style={{ ...studio.td, fontWeight: 600 }}>{report.reporteNombre}</td>
                                        <td style={{ ...studio.td, color: ink.muted }}>{report.viewCount || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                )}

                {hasCatalog && selectedProject && viewMode === 'grid' && (
                    visibleReports.length === 0 ? (
                        <div style={studio.empty}>Ningún reporte coincide.</div>
                    ) : (
                        <div style={m('reportGrid')}>
                            {visibleReports.map((report) => (
                                <button
                                    key={report.id}
                                    type="button"
                                    onClick={() => openReport(report)}
                                    style={home.reportTile}
                                >
                                    <div style={home.reportPreview}>
                                        <img src={ChartLineIcon} alt="" style={{ width: 40, height: 40, opacity: 0.28 }} />
                                    </div>
                                    <div style={{ padding: '12px 14px 14px' }}>
                                        <div style={home.reportTileTitle}>{report.reporteNombre}</div>
                                        <div style={home.popularSub}>{report.viewCount || 0} vistas</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )
                )}

                {hasCatalog && selectedClient && !selectedProject && (
                    visibleProjects.length === 0 ? (
                        <div style={studio.empty}>Ningún proyecto coincide.</div>
                    ) : (
                        <div style={m('entityGrid')}>
                            {visibleProjects.map((project) => (
                                <button
                                    key={project.id}
                                    type="button"
                                    onClick={() => { setSelectedProject(project); setQuery(''); }}
                                    style={home.entityCard}
                                >
                                    <div style={home.logoWrap}>
                                        <img
                                            src={project.logoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(project.nombre)}&background=f4eefc&color=4527a0&size=128`}
                                            alt=""
                                            style={home.logo}
                                        />
                                    </div>
                                    <div style={home.entityName}>{project.nombre}</div>
                                    <div style={home.popularSub}>
                                        {reportCounts[project.id] || 0} {(reportCounts[project.id] || 0) === 1 ? 'reporte' : 'reportes'}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )
                )}

                {hasCatalog && !selectedClient && (
                    visibleClients.length === 0 ? (
                        <div style={studio.empty}>Ningún cliente coincide.</div>
                    ) : (
                        <div style={m('entityGrid')}>
                            {visibleClients.map((client) => (
                                <button
                                    key={client.id}
                                    type="button"
                                    onClick={() => { setSelectedClient(client); setQuery(''); }}
                                    style={home.entityCard}
                                >
                                    <div style={home.logoWrap}>
                                        <img
                                            src={client.logoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(client.nombre)}&background=f4eefc&color=4527a0&size=128`}
                                            alt=""
                                            style={home.logo}
                                        />
                                    </div>
                                    <div style={home.entityName}>{client.nombre}</div>
                                    <div style={home.popularSub}>
                                        {projectCounts[client.id] || 0} {(projectCounts[client.id] || 0) === 1 ? 'proyecto' : 'proyectos'}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

const home = {
    page: {
        ...studio.page,
        padding: '32px 36px 48px',
        maxWidth: 1500,
        margin: '0 auto',
    },
    workRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
        gap: 16,
        alignItems: 'start',
    },
    pendingPanel: {
        backgroundColor: '#ffffff',
        backgroundImage: 'linear-gradient(180deg, #fdf3f1 0%, #ffffff 72px)',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#f0d4cf',
        borderRadius: 18,
        padding: '20px 22px 16px',
        boxShadow: '0 10px 28px rgba(197, 34, 31, 0.06)',
    },
    adjustPanel: {
        backgroundColor: '#ffffff',
        backgroundImage: 'linear-gradient(180deg, #fff8f0 0%, #ffffff 72px)',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#efd7b8',
        borderRadius: 18,
        padding: '20px 22px 16px',
        boxShadow: '0 10px 28px rgba(230, 81, 0, 0.06)',
    },
    pendingHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 16,
    },
    pendingTitle: {
        margin: '6px 0 0',
        fontSize: '1.25rem',
        fontWeight: 600,
        color: ink.text,
        letterSpacing: '-0.03em',
    },
    pendingSub: {
        margin: '6px 0 0',
        fontSize: '0.9rem',
        color: ink.muted,
    },
    pendingCount: {
        minWidth: 44,
        height: 44,
        borderRadius: 14,
        backgroundColor: ink.dangerSoft,
        color: ink.danger,
        fontWeight: 700,
        fontSize: '1.2rem',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        letterSpacing: '-0.03em',
        flexShrink: 0,
    },
    adjustCount: {
        minWidth: 44,
        height: 44,
        borderRadius: 14,
        backgroundColor: '#fff3e0',
        color: '#e65100',
        fontWeight: 700,
        fontSize: '1.2rem',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        letterSpacing: '-0.03em',
        flexShrink: 0,
    },
    pendingList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    pendingCard: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px 14px 18px',
        borderRadius: 14,
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        overflow: 'hidden',
    },
    pendingCardOverdue: {
        backgroundColor: '#fff8f7',
        borderColor: '#f3c7c3',
    },
    pendingCardHot: {
        borderColor: '#e8b4ae',
        boxShadow: '0 6px 16px rgba(197, 34, 31, 0.06)',
    },
    pendingAccent: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        backgroundColor: ink.danger,
    },
    adjustAccent: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        backgroundColor: '#e65100',
    },
    adjustCardHot: {
        borderColor: '#efd7b8',
        boxShadow: '0 6px 16px rgba(230, 81, 0, 0.06)',
    },
    adjustCta: {
        ...studio.primaryBtn,
        padding: '8px 14px',
        fontSize: '0.84rem',
        backgroundColor: '#e65100',
    },
    pendingBody: {
        flex: 1,
        minWidth: 0,
    },
    pendingName: {
        fontWeight: 600,
        color: ink.text,
        fontSize: '0.98rem',
        letterSpacing: '-0.02em',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
    },
    pendingMeta: {
        marginTop: 4,
        fontSize: '0.82rem',
        color: ink.faint,
    },
    pendingAside: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
    },
    dueChip: {
        borderRadius: 999,
        padding: '5px 10px',
        fontSize: '0.74rem',
        fontWeight: 700,
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
    },
    dueChipTone: {
        danger: { backgroundColor: ink.dangerSoft, color: ink.danger },
        warn: { backgroundColor: '#fff3e0', color: '#e65100' },
        ok: { backgroundColor: ink.wash, color: ink.muted },
        muted: { backgroundColor: ink.wash, color: ink.faint },
    },
    blockedChip: {
        borderRadius: 999,
        padding: '2px 8px',
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        backgroundColor: ink.wash,
        color: ink.muted,
    },
    pendingCta: {
        ...studio.primaryBtn,
        padding: '8px 14px',
        fontSize: '0.84rem',
    },
    pendingGhost: {
        ...studio.ghostBtn,
        padding: '8px 14px',
        fontSize: '0.84rem',
    },
    blocked: {
        marginLeft: 8,
        color: ink.danger,
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
    },
    popularGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: 12,
        marginBottom: 8,
    },
    popularCard: {
        appearance: 'none',
        textAlign: 'left',
        backgroundColor: ink.surface,
        border: `1px solid ${ink.line}`,
        borderRadius: 16,
        padding: '16px 18px 18px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        borderLeft: `3px solid ${ink.accent}`,
    },
    popularMeta: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
    },
    rank: {
        fontSize: '0.72rem',
        fontWeight: 700,
        color: ink.accentText,
        letterSpacing: '0.06em',
    },
    viewsBadge: {
        fontSize: '0.72rem',
        fontWeight: 600,
        color: ink.faint,
        backgroundColor: ink.wash,
        borderRadius: 999,
        padding: '2px 8px',
    },
    popularTitle: {
        fontWeight: 600,
        color: ink.text,
        fontSize: '0.98rem',
        letterSpacing: '-0.02em',
        lineHeight: 1.3,
    },
    popularSub: {
        marginTop: 6,
        fontSize: '0.8rem',
        color: ink.faint,
    },
    crumbs: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 8,
        flexWrap: 'wrap',
    },
    crumbBtn: {
        appearance: 'none',
        border: 'none',
        background: 'transparent',
        padding: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '0.82rem',
        fontWeight: 600,
        color: ink.accentText,
    },
    crumbSep: { color: ink.faint, fontSize: '0.82rem' },
    crumbHere: { color: ink.muted, fontSize: '0.82rem', fontWeight: 600 },
    scopeOn: {
        appearance: 'none',
        border: 'none',
        borderRadius: 8,
        padding: '7px 12px',
        fontSize: '0.82rem',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        backgroundColor: ink.surface,
        color: ink.accentText,
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    },
    scopeOff: {
        appearance: 'none',
        border: 'none',
        borderRadius: 8,
        padding: '7px 12px',
        fontSize: '0.82rem',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        color: ink.muted,
    },
    entityGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 12,
    },
    entityCard: {
        appearance: 'none',
        textAlign: 'center',
        backgroundColor: ink.surface,
        border: `1px solid ${ink.line}`,
        borderRadius: 16,
        padding: 16,
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
    },
    logoWrap: {
        width: '100%',
        height: 72,
        borderRadius: 12,
        backgroundColor: ink.wash,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    logo: {
        maxWidth: '80%',
        maxHeight: 56,
        objectFit: 'contain',
    },
    entityName: {
        fontWeight: 600,
        color: ink.text,
        fontSize: '0.95rem',
        letterSpacing: '-0.02em',
    },
    reportGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: 12,
    },
    reportTile: {
        appearance: 'none',
        textAlign: 'left',
        backgroundColor: ink.surface,
        border: `1px solid ${ink.line}`,
        borderRadius: 14,
        padding: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        overflow: 'hidden',
    },
    reportPreview: {
        height: 110,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: ink.wash,
        borderBottom: `1px solid ${ink.line}`,
    },
    reportTileTitle: {
        fontWeight: 600,
        color: ink.text,
        fontSize: '0.92rem',
    },
};

/* Sobrescrituras para pantallas angostas (≤ NARROW_MAX). */
const homeNarrow = {
    page: { padding: '16px 12px 32px' },
    workRow: { gridTemplateColumns: 'minmax(0, 1fr)', gap: 12 },
    pendingPanel: { padding: '16px 14px 12px', borderRadius: 16 },
    adjustPanel: { padding: '16px 14px 12px', borderRadius: 16 },
    pendingCard: { flexWrap: 'wrap', gap: 10, padding: '12px 12px 12px 16px' },
    pendingAside: { width: '100%', justifyContent: 'space-between' },
    pendingCta: { minHeight: 40, padding: '8px 18px' },
    pendingGhost: { minHeight: 40, padding: '8px 18px' },
    adjustCta: { minHeight: 40, padding: '8px 18px' },
    popularGrid: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 },
    popularCard: { padding: '12px 12px 14px' },
    reportGrid: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 },
    entityGrid: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 },
};

export default DashboardGrid;
